import type { ComponentPropsWithoutRef } from "react";
import type { ExtraProps } from "react-markdown";
import { responsiveImage } from "@/lib/responsive-image";

/**
 * Inline figure for Markdown article bodies. Article images are 16:9 exports,
 * so the box reserves that ratio before the file arrives (`auto` hands over
 * to the intrinsic ratio once it loads) and lazy images no longer push the
 * text below them down while the reader scrolls. Sources go through
 * `responsiveImage` so a 1.5 MB PNG is served as a right-sized WebP.
 */
export function ArticleImage({
  src,
  alt,
  node: _node,
  ...rest
}: ComponentPropsWithoutRef<"img"> & ExtraProps) {
  if (typeof src !== "string" || !src) return null;
  const image = responsiveImage(src, [640, 960, 1280, 1600]);
  return (
    <img
      {...rest}
      src={image.src}
      srcSet={image.srcSet}
      sizes={image.srcSet ? "(min-width: 1024px) 768px, 100vw" : undefined}
      width={image.width}
      height={image.height}
      alt={alt ?? ""}
      loading="lazy"
      decoding="async"
      className="my-8 h-auto w-full rounded-xl border border-border bg-card [aspect-ratio:auto_16/9]"
    />
  );
}
